import type { ReactElement } from 'react';
import type { PostStatusDto } from '@gr/core/content/posts/dto';
import { ErrorCode } from '@gr/core/errors';
import { META_STATUSES } from '@gr/core/content/posts/meta-status';
import { useT } from '../../../../app/di';
import { Button, IconButton } from '../../../../ui-kit/Button/Button';
import { FieldRow, FieldRowActions, FieldRowGrow } from '../../../../ui-kit/FieldRow/FieldRow';
import { FormAlert, FormSelectField, FormTextField, useServerForm } from '../../../../ui-kit/form/form';
import type { ServerOutcome } from '../../../../ui-kit/form/server-errors';
import { normalizedStatus, STATUS_FORM_FIELDS, statusValuesOf, type StatusValues } from '../model/status-form';
import styles from './StatusItem.module.scss';

export interface StatusItemProps {
  status: PostStatusDto;
  disabled: boolean;
  onSave(values: StatusValues): Promise<ServerOutcome>;
  onDelete(): void;
  onUp?: () => void;
  onDown?: () => void;
}

/** POST-UC-9: статус в списке со своей формой; правка сохраняется кнопкой, появляющейся после изменения. */
export function StatusItem({ status, disabled, onSave, onDelete, onUp, onDown }: StatusItemProps): ReactElement {
  const { t } = useT();
  const values = statusValuesOf(status);
  const { form, submit } = useServerForm<StatusValues>({ defaultValues: values, values, fields: STATUS_FORM_FIELDS, onSubmit: (v) => onSave(normalizedStatus(v)) });
  const { control, formState } = form;
  const metaOptions = META_STATUSES.map((meta) => ({ value: meta, label: t(`posts.meta.${meta}`) }));
  return (
    <li className={styles.item}>
      <form onSubmit={submit} noValidate aria-label={status.name}>
        <FieldRow>
          <FieldRowGrow>
            <FormTextField control={control} name="name" label={t('posts.statuses.name')} hint={t('posts.statuses.count', { n: status.postCount })} rules={{ required: ErrorCode.FieldRequired }} />
          </FieldRowGrow>
          <FormTextField control={control} name="color" label={t('posts.statuses.color')} type="color" />
          <FormSelectField control={control} name="meta" label={t('posts.statuses.meta')} options={metaOptions} />
          <FieldRowActions end>
            <IconButton size="sm" icon="up" label={t('posts.statuses.moveUp')} disabled={!onUp || disabled} onClick={onUp} />
            <IconButton size="sm" icon="down" label={t('posts.statuses.moveDown')} disabled={!onDown || disabled} onClick={onDown} />
            {formState.isDirty ? (
              <Button size="sm" type="submit" variant="primary" disabled={disabled} loading={formState.isSubmitting}>
                {t('common.save')}
              </Button>
            ) : null}
            <IconButton size="sm" icon="trash" label={t('common.delete')} disabled={disabled} onClick={onDelete} />
          </FieldRowActions>
        </FieldRow>
      </form>
      <FormAlert form={form} />
    </li>
  );
}
